'use client';

import { Accordion, Flex, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { Slider } from '@/components/ui/slider';

const FilterBox = ({
  filterData,
  title,
  handleSelectFilter,
  selectedFilters,
  slug,
}: {
  filterData:
    | Array<{ name: string; count: number }>
    | { min: number; max: number }
    | Array<{ name: string; hex?: string; count: number }>;
  title: string;
  handleSelectFilter: (filterType: string, value: string | number[]) => void;
  slug: string;
  selectedFilters: {
    [key: string]: string[];
  };
}) => {
  if (slug === 'preco' && !Array.isArray(filterData)) {
    return (
      <PriceRangeFilter
        title={title}
        slug={slug}
        range={filterData}
        selected={selectedFilters.preco}
        handleSelectFilter={handleSelectFilter}
      />
    );
  }

  return (
    <Accordion.Root collapsible defaultValue={[title]}>
      <Accordion.Item value={title}>
        <Accordion.ItemTrigger>
          <Text
            fontWeight="700"
            fontSize="13px"
            color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
            flex="1"
          >
            {title}
          </Text>
          <Accordion.ItemIndicator />
        </Accordion.ItemTrigger>
        <Accordion.ItemContent>
          <Accordion.ItemBody>
            <Flex flexDir="column" gap="2px" pl="4px">
              {Array.isArray(filterData) &&
                filterData.map((item) => {
                  const domId = `${slug}-${item.name}`.toLowerCase();
                  const hex = 'hex' in item ? item.hex : undefined;
                  return (
                    <Flex
                      key={domId}
                      align="center"
                      gap="8px"
                      py="5px"
                      px="6px"
                      borderRadius="8px"
                      cursor="pointer"
                      transition="background 0.15s"
                      _hover={{ bg: 'rgba(0,176,202,0.06)' }}
                    >
                      {hex && (
                        <span
                          style={{
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            background: hex,
                            border: '1px solid rgba(0,0,0,0.15)',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <input
                        type="checkbox"
                        id={domId}
                        name={domId}
                        value={item.name}
                        checked={
                          selectedFilters[slug]
                            ? selectedFilters[slug].includes(
                                item.name.toLocaleLowerCase(),
                              )
                            : false
                        }
                        onChange={() =>
                          handleSelectFilter(slug, item.name.toLocaleLowerCase())
                        }
                        style={{
                          width: '14px',
                          height: '14px',
                          accentColor: '#00B0CA',
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      />
                      <label
                        htmlFor={domId}
                        style={{ fontSize: '13px', cursor: 'pointer', lineHeight: '1.4' }}
                      >
                        {item.name}{' '}
                        <span style={{ fontSize: '11px', opacity: 0.5 }}>
                          ({item.count})
                        </span>
                      </label>
                    </Flex>
                  );
                })}
            </Flex>
          </Accordion.ItemBody>
        </Accordion.ItemContent>
      </Accordion.Item>
    </Accordion.Root>
  );
};

const PriceRangeFilter = ({
  title,
  slug,
  range,
  selected,
  handleSelectFilter,
}: {
  title: string;
  slug: string;
  range: { min: number; max: number };
  selected: string[] | undefined;
  handleSelectFilter: (filterType: string, value: string | number[]) => void;
}) => {
  const initial: [number, number] = selected && selected.length === 2
    ? [Number(selected[0]), Number(selected[1])]
    : [range.min, range.max];

  const [localValue, setLocalValue] = useState<[number, number]>(initial);

  const currency = (value: number) =>
    new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(
      value,
    );

  return (
    <Accordion.Root collapsible defaultValue={[title]}>
      <Accordion.Item value={title}>
        <Accordion.ItemTrigger>
          <Text
            fontWeight="700"
            fontSize="13px"
            color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
            flex="1"
          >
            {title}
          </Text>
          <Accordion.ItemIndicator />
        </Accordion.ItemTrigger>
        <Accordion.ItemContent>
          <Accordion.ItemBody>
            <Flex flexDir="column" gap="12px" pl="4px" pr="8px" pt="8px">
              <Flex justify="space-between">
                <Text fontSize="12px" color="rgba(0,0,0,0.6)" _dark={{ color: 'rgba(255,255,255,0.6)' }}>
                  {currency(localValue[0])}
                </Text>
                <Text fontSize="12px" color="rgba(0,0,0,0.6)" _dark={{ color: 'rgba(255,255,255,0.6)' }}>
                  {currency(localValue[1])}
                </Text>
              </Flex>
              <Slider
                min={range.min}
                max={range.max}
                step={1}
                value={localValue}
                onValueChange={(e) => setLocalValue(e.value as [number, number])}
                onValueChangeEnd={(e) =>
                  handleSelectFilter(slug, e.value as number[])
                }
                colorPalette="cyan"
              />
            </Flex>
          </Accordion.ItemBody>
        </Accordion.ItemContent>
      </Accordion.Item>
    </Accordion.Root>
  );
};

export default FilterBox;
